import { observer } from 'src/utils/mobx-react';

import {
    PopupDescription,
    LinkWrapper,
} from 'src/domains/players/webview/components/Account/Notifications/BlockingNotifications.style';
import React from 'react';
import { UniverseComponent } from 'src/domains/common/universes';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { GamblingSafetyTopBar } from 'src/domains/layouts/webview/components/gamblingSafetyTopBar/GamblingSafetyTopBar';
import { Footer } from 'src/domains/layouts/webview/modules/layoutSkeleton/Footer';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import {
    BodySection,
    ClosureOverlay,
    ClosureStack,
    Content,
    FooterBlock,
    GamblingSafetyTopSlot,
    LogoRow,
    PopupHeader,
    SectionHeading,
} from './ClosureModeComponent.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { FooterCopyright } from 'src/domains/layouts/webview/modules/layoutSkeleton/footer/copyright/FooterCopyright';

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
] as const;

const formatClosureDate = (date: string): string => {
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (isoMatch === null) {
        return date;
    }

    const year = isoMatch[1];
    const monthIndex = Number(isoMatch[2]) - 1;
    const day = Number(isoMatch[3]);
    const month = MONTHS[monthIndex];

    if (month === undefined || Number.isNaN(day)) {
        return date;
    }

    return `${day} ${month} ${year}`;
};

const getClosureDateLabel = (
    depositsTurnOffForLeavingOperator: { enabled: false } | { enabled: true; date: string; operatorName: string }
): string => {
    if (depositsTurnOffForLeavingOperator.enabled === false) {
        return '';
    }

    return formatClosureDate(depositsTurnOffForLeavingOperator.date);
};

export const ClosureModeComponent = observer(
    'ClosureModeComponent',
    ({ alreadyClosed = false }: { alreadyClosed?: boolean }): JSX.Element | null => {
        const common = useCommon();
        const router = StarRouter.get(common);
        const { isSaferGambling, operatorFullName, accountHelperMail, depositsTurnOffForLeavingOperator } =
            ConfigComponents.get(common).config;
        const showGamblingSafetyStrip = isSaferGambling.available === true;
        const mailtoSupport = `mailto:${accountHelperMail}`;
        const closureDate = getClosureDateLabel(depositsTurnOffForLeavingOperator);

        return (
            <ClosureOverlay
                data-test='overlay-prompt'
                alreadyClosed={alreadyClosed}
            >
                <ClosureStack>
                    {showGamblingSafetyStrip ? (
                        <GamblingSafetyTopSlot>
                            <GamblingSafetyTopBar />
                        </GamblingSafetyTopSlot>
                    ) : null}
                    <Content>
                        <LogoRow>
                            <UniverseComponent>
                                {(universeModule): React.ReactElement => <universeModule.compliancePopupLogo />}
                            </UniverseComponent>
                        </LogoRow>

                        <BodySection>
                            {alreadyClosed ? (
                                <>
                                    <PopupHeader>
                                        <I18n
                                            langKey='account.closure.title'
                                            defaultText='{operatorFullName} has closed'
                                            params={{ operatorFullName }}
                                        />
                                    </PopupHeader>
                                    <PopupDescription>
                                        <I18n
                                            langKey='account.closure.intro'
                                            defaultText='{operatorFullName} closed on {closureDate}. We want to thank our customers for their support over the years.'
                                            params={{ operatorFullName, closureDate }}
                                        />
                                    </PopupDescription>
                                    <PopupDescription>
                                        <I18n
                                            langKey='account.closure.support.before-email'
                                            defaultText='For any queries relating to your former {operatorFullName} account, your balance, or open bets, contact '
                                            params={{ operatorFullName }}
                                        />
                                        <LinkWrapper to={mailtoSupport}>
                                            <I18n
                                                langKey='account.closure.support-email'
                                                defaultText='{accountHelperMail}'
                                                params={{ accountHelperMail }}
                                            />
                                        </LinkWrapper>
                                    </PopupDescription>
                                </>
                            ) : (
                                <>
                                    <PopupHeader>
                                        <I18n
                                            langKey='account.closure-with-login.title'
                                            defaultText='{operatorFullName} is Closing'
                                            params={{ operatorFullName }}
                                        />
                                    </PopupHeader>
                                    <PopupDescription>
                                        <I18n
                                            langKey='account.closure-with-login.description-part-1'
                                            defaultText='After careful consideration, we have made the difficult decision to cease operations on {closureDate}. We want to take this opportunity to sincerely thank all of our customers for their loyalty and support.'
                                            params={{ closureDate }}
                                        />
                                    </PopupDescription>
                                    <PopupDescription>
                                        <I18n
                                            langKey='account.closure-with-login.description-part-2'
                                            defaultText='Please take a moment to read the important information below.'
                                        />
                                    </PopupDescription>

                                    <SectionHeading>
                                        <I18n
                                            langKey='account.closure-with-login.section-withdraw-heading'
                                            defaultText='Withdraw Your Funds'
                                        />
                                    </SectionHeading>
                                    <PopupDescription>
                                        <I18n
                                            langKey='account.closure-with-login.withdraw-description'
                                            defaultText="Please ensure you withdraw any funds held in your {operatorFullName} account before the closure date of {closureDate}. After this date, the platform will no longer be accessible. If you need any assistance processing a withdrawal, please don't hesitate to get in touch."
                                            params={{ operatorFullName, closureDate }}
                                        />
                                    </PopupDescription>

                                    <SectionHeading>
                                        <I18n
                                            langKey='account.closure-with-login.section-bets-heading'
                                            defaultText='Open & Ante-Post Bets'
                                        />
                                    </SectionHeading>
                                    <PopupDescription>
                                        <I18n
                                            langKey='account.closure-with-login.bets-description'
                                            defaultText='We will honour all open and ante-post bets placed before the closure date. Any winnings that settle before {closureDate} will be credited to your account in the usual way. Any winnings that settle after this date, will be manually sent to your primary payment method. Please ensure you check your account and withdraw any balances ahead of {closureDate}. If there are any issues or concerns, please speak to us.'
                                            params={{ closureDate }}
                                        />
                                    </PopupDescription>

                                    <SectionHeading>
                                        <I18n
                                            langKey='account.closure-with-login.section-support-heading'
                                            defaultText="Questions? We're Here to Help"
                                        />
                                    </SectionHeading>
                                    <PopupDescription>
                                        <I18n
                                            langKey='account.closure-with-login.support-part-1'
                                            defaultText='Our customer support team remains available to assist you with any queries or concerns during this transition period. Please reach out to us at'
                                        />{' '}
                                        <LinkWrapper to={mailtoSupport}>
                                            <I18n
                                                langKey='account.closure-with-login.support-email'
                                                defaultText='{accountHelperMail}'
                                                params={{ accountHelperMail }}
                                            />
                                        </LinkWrapper>{' '}
                                        <I18n
                                            langKey='account.closure-with-login.support-part-2'
                                            defaultText='and we will respond as promptly as possible.'
                                        />
                                    </PopupDescription>
                                </>
                            )}
                            <PopupDescription>
                                <I18n
                                    langKey='account.closure.thank-you'
                                    defaultText='Thank you for being a {operatorFullName} customer.'
                                    params={{ operatorFullName }}
                                />
                            </PopupDescription>
                            <PopupDescription>
                                <I18n
                                    langKey='account.closure.signature'
                                    defaultText='The {operatorFullName} Team.'
                                    params={{ operatorFullName }}
                                />
                            </PopupDescription>
                        </BodySection>
                    </Content>

                    <FooterBlock alreadyClosed={alreadyClosed}>
                        {alreadyClosed ? (
                            <FooterCopyright alreadyClosed={alreadyClosed} />
                        ) : (
                            <Footer currentView={router.currentView} />
                        )}
                    </FooterBlock>
                </ClosureStack>
            </ClosureOverlay>
        );
    }
);
